import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Alert,
} from "reactstrap"
import { getStatesOptions, SelectOption } from "helpers/dropdownsApi"
import { getUserApi, getKycAssetUrl } from "helpers/auth_api"
import apiClient from "helpers/apiClient"
import { formatIndianNumber, parseIndianNumber, formatBidPrice } from "helpers/indianNumber"
import { formatIndianDate } from "helpers/indianDate"
import { splitClientName } from "helpers/biddingAccess"
import {
  AGREEMENT_HEADER,
  BIDDER_DETAILS,
  PROPERTY_DETAILS,
  AGREEMENT_CLAUSES,
  DISPUTE_RESOLUTION_DETAILS,
  CHECKBOX_AGREEMENT_TEXT,
  EMAIL_AGREEMENT_INFO,
} from "./BiddingAgreementContent"
import "./BidRequestModal.scss"

const BIDS_PLACE_URL = "/api/bids/listing"

const STEPS = [
  { id: 0, label: "Welcome" },
  { id: 1, label: "Contact" },
  { id: 2, label: "Bid" },
  { id: 3, label: "Terms" },
]

/** Hint for quick-bid / prefill: next whole rupee above current (optional, not enforced in UI). */
const nextWholeRupeeAbove = (amount: number) =>
  Number.isFinite(amount) ? Math.floor(amount) + 1 : 0

interface BidRequestModalProps {
  isOpen: boolean
  toggle: () => void
  listingId?: string
  startingBid?: string
  currentHighestBid?: number | null
  minBidIncrease?: number
  listingStatus?: string
  reservePrice?: number
  pricePerSqFt?: number
  roiPercentage?: number
  superAreaSqFt?: number
  annualRent?: number
  onSuccess?: () => void
  propertyTitle?: string
  propertyAddress?: string
  auctionDate?: string
  userHasBid?: boolean
}

interface UserKycData {
  kycSelfieUrl?: string
  kycSignatureUrl?: string
  profileImageUrl?: string
}

const BidRequestModal: React.FC<BidRequestModalProps> = ({
  isOpen,
  toggle,
  listingId,
  startingBid = "15000",
  currentHighestBid,
  minBidIncrease,
  listingStatus,
  reservePrice,
  pricePerSqFt,
  roiPercentage,
  superAreaSqFt,
  annualRent,
  onSuccess,
  propertyTitle,
  propertyAddress,
  auctionDate,
  userHasBid = false,
}) => {
  const isPreRented = typeof listingStatus === "string" && /pre-rented|prerented/i.test(listingStatus.trim())
  // If user has already bid, skip Welcome and Contact steps - go directly to Bid (step 2)
  const initialStep = userHasBid ? 2 : 0
  const [currentStep, setCurrentStep] = useState(initialStep)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([])
  const [userKycData, setUserKycData] = useState<UserKycData>({})
  const agreementRef = useRef<HTMLDivElement>(null)

  // Check if user is a broker
  const [isBroker, setIsBroker] = useState(false)
  useEffect(() => {
    try {
      const authUserStr = localStorage.getItem("authUser")
      if (authUserStr) {
        const user = JSON.parse(authUserStr)
        const role = (user.role || "").toLowerCase().trim()
        setIsBroker(role === "broker")
      }
    } catch (_) { }
  }, [])

  // Broker-specific: Client first and last name
  const [clientFirstName, setClientFirstName] = useState("")
  const [clientLastName, setClientLastName] = useState("")
  const clientName = [clientFirstName.trim(), clientLastName.trim()].filter(Boolean).join(" ")

  // Step 1 – Contact Information
  const [fullName, setFullName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [email, setEmail] = useState("")
  const [state, setState] = useState("")
  const [city, setCity] = useState("")
  const [pinCode, setPinCode] = useState("")
  const [address, setAddress] = useState("")

  // Step 2 – Bid Request (reserve, or strictly above current highest if user already bid; Super Area fixed from listing)
  const [finalBidAmount, setFinalBidAmount] = useState("15000")
  const [timeOfClosure, setTimeOfClosure] = useState("")
  const [pricePerSqFtInput, setPricePerSqFtInput] = useState("")
  const [roiPercentageInput, setRoiPercentageInput] = useState("")
  const [description, setDescription] = useState("")

  // Step 3 – Terms
  const [termsAccepted, setTermsAccepted] = useState(false)

  const reserveFloor =
    reservePrice != null && Number.isFinite(reservePrice)
      ? reservePrice
      : Number(parseIndianNumber(startingBid)) || 0
  const suggestedBidTotal =
    currentHighestBid != null && Number.isFinite(currentHighestBid)
      ? nextWholeRupeeAbove(currentHighestBid)
      : reserveFloor > 0
        ? reserveFloor
        : 1

  // Super area is fixed from listing (same as property create)
  const effectiveArea = superAreaSqFt != null && superAreaSqFt > 0 ? superAreaSqFt : 0

  // No reserve/current-high floor — any positive amount is allowed server-side
  const applyPrice = (price: number) => {
    const clamped = Math.max(price, 0)
    setFinalBidAmount(String(Math.round(clamped)))
    if (effectiveArea > 0 && clamped > 0) {
      setPricePerSqFtInput(String(Math.round(clamped / effectiveArea)))
    }
    if (isPreRented && annualRent != null && annualRent > 0 && clamped > 0) {
      setRoiPercentageInput(((annualRent / clamped) * 100).toFixed(2))
    }
  }

  const handlePricePerSqFtChange = (rawStr: string) => {
    const raw = parseIndianNumber(rawStr)
    setPricePerSqFtInput(raw)
    if (raw === "") return
    const psf = Number(raw)
    if (effectiveArea > 0 && psf > 0) {
      const price = psf * effectiveArea
      const clamped = Math.max(price, 0)
      setFinalBidAmount(String(Math.round(clamped)))
      if (isPreRented && annualRent != null && annualRent > 0 && clamped > 0) {
        setRoiPercentageInput(((annualRent / clamped) * 100).toFixed(2))
      }
    }
  }
  const handleTotalPriceChange = (rawStr: string) => {
    const raw = parseIndianNumber(rawStr)
    setFinalBidAmount(raw)
    if (raw === "") return
    const price = Number(raw)
    if (price <= 0) return
    if (effectiveArea > 0) {
      setPricePerSqFtInput(String(Math.round(price / effectiveArea)))
    }
    if (isPreRented && annualRent != null && annualRent > 0) {
      setRoiPercentageInput(((annualRent / price) * 100).toFixed(2))
    }
  }
  const handleRoiChange = (rawStr: string) => {
    setRoiPercentageInput(rawStr)
    const roiPercent = Number(rawStr)
    if (isPreRented && annualRent != null && annualRent > 0 && roiPercent > 0) {
      const roi = roiPercent / 100
      const price = annualRent / roi // price = annual/roi
      applyPrice(price)
    }
  }

  // Quick bid options (minBidIncrease is optional suggestion, not a floor)
  const minInc = minBidIncrease ?? 0
  const quickOpt1 = suggestedBidTotal
  const quickOpt2 = suggestedBidTotal + (minInc > 0 ? minInc : 1)
  const quickOpt3 = suggestedBidTotal + 2 * (minInc > 0 ? minInc : 1)

  const handleQuickBid = (amount: number) => {
    setFinalBidAmount(String(Math.round(amount)))
    if (effectiveArea > 0 && amount > 0) {
      setPricePerSqFtInput(String(Math.round(amount / effectiveArea)))
    }
    if (isPreRented && annualRent != null && annualRent > 0 && amount > 0) {
      setRoiPercentageInput(((annualRent / amount) * 100).toFixed(2))
    }
  }

  /** Starting bid for ROI step ratio: same as reserve / listing floor. */
  const startingBidPriceForRoi =
    reserveFloor > 0 ? reserveFloor : Number(parseIndianNumber(startingBid)) || 0
  /**
   * One ROI step (percentage points) = (minimum increment ÷ starting bid) × 100.
   * Used by + / − ROI buttons so each click matches one “increment” in price terms at the starting level.
   */
  const roiStepPercent =
    minInc > 0 && startingBidPriceForRoi > 0
      ? (minInc / startingBidPriceForRoi) * 100
      : 0.25

  const getCurrentRoiPercent = (): number => {
    const raw = roiPercentageInput.trim()
    if (raw !== "") {
      const p = Number(raw)
      if (Number.isFinite(p)) return p
    }
    const amt = Number(parseIndianNumber(finalBidAmount))
    if (annualRent != null && annualRent > 0 && amt > 0) return (annualRent / amt) * 100
    if (roiPercentage != null && Number.isFinite(roiPercentage)) return roiPercentage
    return 0
  }

  const handleRoiStep = (direction: 1 | -1) => {
    if (!isPreRented || annualRent == null || annualRent <= 0) return
    const cur = getCurrentRoiPercent()
    let next = cur + direction * roiStepPercent
    next = Math.max(0.01, Math.min(99.99, next))
    handleRoiChange(next.toFixed(4))
  }

  /** ROI % at minimum bid total (annual rent ÷ min bid). Shown on Min ROI button. */
  const minBidRoiPercent =
    annualRent != null && annualRent > 0 && suggestedBidTotal > 0
      ? (annualRent / suggestedBidTotal) * 100
      : 0

  useEffect(() => {
    getStatesOptions().then(setStateOptions).catch(() => setStateOptions([]))
  }, [])

  // Reset to initial step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(userHasBid ? 2 : 0)
    }
  }, [isOpen, userHasBid])

  // Auto-fill contact information from logged-in user profile when modal opens
  useEffect(() => {
    if (!isOpen) return
    const authUserStr = localStorage.getItem("authUser")
    if (!authUserStr) return
    try {
      const obj = JSON.parse(authUserStr)
      const uid = String(obj.uid ?? obj._id ?? "").trim()
      if (!uid) return
      getUserApi(uid)
        .then((res) => {
          if (res?.success && res?.data) {
            const d = res.data as Record<string, unknown>
            if (d.fullName) setFullName(String(d.fullName))
            if (d.email) setEmail(String(d.email))
            if (d.mobileNumber) setPhoneNumber(String(d.mobileNumber))
            if (d.state) setState(String(d.state))
            if (d.city) setCity(String(d.city))
            if (d.address) setAddress(String(d.address))
            if (d.pinCode) setPinCode(String(d.pinCode))
            setUserKycData({
              kycSelfieUrl: d.kycSelfieUrl as string | undefined,
              kycSignatureUrl: d.kycSignatureUrl as string | undefined,
              profileImageUrl: d.profileImageUrl as string | undefined,
            })
            // For brokers, fetch last used client name from previous bids
            const userRole = String(d.role || "").toLowerCase().trim()
            if (userRole === "broker") {
              apiClient.get("/api/bids/my-bids")
                .then((bidsRes: any) => {
                  if (bidsRes?.data?.success && Array.isArray(bidsRes.data.data)) {
                    const bidWithClient = bidsRes.data.data.find(
                      (b: any) =>
                        (b.clientFirstName && b.clientFirstName.trim()) ||
                        (b.clientName && b.clientName.trim())
                    )
                    if (bidWithClient?.clientFirstName) {
                      setClientFirstName(String(bidWithClient.clientFirstName))
                      setClientLastName(String(bidWithClient.clientLastName || ""))
                    } else if (bidWithClient?.clientName) {
                      const { first, last } = splitClientName(String(bidWithClient.clientName))
                      setClientFirstName(first)
                      setClientLastName(last)
                    } else if (d.fullName) {
                      const { first, last } = splitClientName(String(d.fullName))
                      setClientFirstName(first)
                      setClientLastName(last)
                    }
                  }
                })
                .catch(() => {
                  if (d.fullName) {
                    const { first, last } = splitClientName(String(d.fullName))
                    setClientFirstName(first)
                    setClientLastName(last)
                  }
                })
            }
          }
        })
        .catch(() => { })
    } catch (_) { }
  }, [isOpen])

  // When modal opens, pre-fill with a suggested amount (user may enter any positive value)
  useEffect(() => {
    if (!isOpen || !listingId) return
    const reserve = reservePrice != null && Number.isFinite(reservePrice)
      ? reservePrice
      : Number(parseIndianNumber(startingBid)) || 0
    const minTotal =
      currentHighestBid != null && Number.isFinite(currentHighestBid)
        ? nextWholeRupeeAbove(currentHighestBid)
        : reserve > 0
          ? reserve
          : 1
    const area = superAreaSqFt != null && superAreaSqFt > 0 ? superAreaSqFt : 0
    setFinalBidAmount(String(Math.round(minTotal)))
    if (area > 0 && minTotal > 0) setPricePerSqFtInput(String(Math.round(minTotal / area)))
    else setPricePerSqFtInput(pricePerSqFt != null && Number.isFinite(pricePerSqFt) ? String(pricePerSqFt) : "")
    if (isPreRented && annualRent != null && annualRent > 0 && minTotal > 0) setRoiPercentageInput(((annualRent / minTotal) * 100).toFixed(2))
    else setRoiPercentageInput(roiPercentage != null && Number.isFinite(roiPercentage) ? String(roiPercentage) : "")
  }, [isOpen, listingId, currentHighestBid, userHasBid, reservePrice, startingBid, superAreaSqFt, annualRent, isPreRented, pricePerSqFt, roiPercentage])

  const goNext = () => {
    setError(null)
    if (currentStep === 0) {
      setCurrentStep(1)
      return
    }
    if (currentStep === 1) {
      if (!fullName.trim() || !phoneNumber.trim() || !email.trim()) {
        setError("Please fill Full Name, Phone Number and Email.")
        return
      }
      setCurrentStep(2)
      return
    }
    if (currentStep === 2) {
      const amount = Number(finalBidAmount)
      if (!Number.isFinite(amount) || amount <= 0) {
        setError("Please enter a valid bid amount.")
        return
      }
      if (!timeOfClosure || !timeOfClosure.trim()) {
        setError("Please enter Time of closure.")
        return
      }
      if (isBroker && (!clientFirstName.trim() || !clientLastName.trim())) {
        setError("Please enter client first name and last name.")
        return
      }
      setCurrentStep(3)
      return
    }
  }

  const goBack = () => {
    setError(null)
    // Allow normal back navigation to all steps
    if (currentStep > 0) setCurrentStep(currentStep - 1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!termsAccepted) {
      setError("Please accept the Bidding Agreement Terms to submit.")
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (listingId) {
        const amount = Number(parseIndianNumber(finalBidAmount))
        if (!Number.isFinite(amount) || amount <= 0) {
          setError("Please enter a valid bid amount.")
          setSubmitting(false)
          return
        }
        if (!timeOfClosure || !timeOfClosure.trim()) {
          setError("Please enter Time of closure.")
          setSubmitting(false)
          return
        }
        const payload: Record<string, unknown> = {
          amount,
          timeOfClosure: timeOfClosure.trim(),
          fullName: fullName.trim(),
          phoneNumber: phoneNumber.trim(),
          email: email.trim(),
          state: state.trim(),
          city: city.trim(),
          pinCode: pinCode.trim(),
          address: address.trim(),
          description: description.trim(),
        }
        if (isBroker) {
          payload.clientFirstName = clientFirstName.trim()
          payload.clientLastName = clientLastName.trim()
          if (clientName) payload.clientName = clientName
        }
        const pricePerSqFtNum = pricePerSqFtInput.trim() !== "" ? Number(parseIndianNumber(pricePerSqFtInput)) : undefined
        if (pricePerSqFtNum != null && Number.isFinite(pricePerSqFtNum)) payload.pricePerSqFt = pricePerSqFtNum
        const roiNum = roiPercentageInput.trim() !== "" ? Number(roiPercentageInput) : undefined
        if (roiNum != null && Number.isFinite(roiNum)) payload.roiPercentage = roiNum
        const res = await apiClient.post<{ success?: boolean; message?: string }>(`${BIDS_PLACE_URL}/${listingId}`, payload)
        if (!res.data?.success) {
          setError(res.data?.message || "Failed to place bid.")
          setSubmitting(false)
          return
        }
      }
      onSuccess?.()
      toggle()
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong."
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setCurrentStep(userHasBid ? 2 : 0)
    setError(null)
    setTermsAccepted(false)
    setClientFirstName("")
    setClientLastName("")
    toggle()
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="xl"
      className="bid-request-modal"
    >
      <ModalHeader toggle={handleClose} className="bid-request-modal-header">
        <span className="bid-request-modal-title">Bid Request – {STEPS[currentStep]?.label ?? currentStep} </span>
        <span className="bid-request-modal-help"> Need Help? Please Contact Customer Service at 900-000-0000</span>
      </ModalHeader>
      <ModalBody className="bid-request-modal-body">
        {/* <h4 className="mb-3">Bid Request</h4> */}

        {/* Step indicator */}
        <div className="bid-request-steps mb-4">
          {STEPS.map((s) => (
            <span
              key={s.id}
              className={`step-pill ${currentStep > s.id ? "done" : ""} ${currentStep === s.id ? "active" : ""}`}
            >
              {currentStep > s.id ? "✓ " : ""}{s.label}
            </span>
          ))}
        </div>

        {error && (
          <Alert color="danger" toggle={() => setError(null)} className="mb-3">
            {error}
            {error.includes("KYC") || error.includes("selfie and signature") ? (
              <span className="ms-1">
                {" "}
                <Link to="/profile" className="alert-link fw-semibold text-end" onClick={toggle}>
                  Go to Profile
                </Link>
              </span>
            ) : null}
          </Alert>
        )}

        {/* Step 0 – Welcome */}
        {currentStep === 0 && (
          <div className="bid-request-welcome card border-0">
            <div className="card-body">
              <div className="welcome-header">
                <div className="welcome-icon">
                  <i className="mdi mdi-gavel" />
                </div>
                <div>
                  <h5 className="mb-1">Welcome to Bidding Registration</h5>
                  <p className="text-muted mb-0">
                    A secure way to participate in the online commercial real estate marketplace.
                  </p>
                </div>
              </div>
              <ul className="mb-3 mt-4">
                <li>By registering to bid, you must close if you win the auction.</li>
                <li>The ability to bid runs for the entirety of the auction's duration.</li>
                <li>Your information is 100% private.</li>
                <li>Your registration will be approved within 12 hours.</li>
                <li>Clicking &quot;Next&quot; will save that section.</li>
              </ul>
              <p className="mb-3">
                Need more info? Check out the <strong>Help Center</strong>.
              </p>

              {/* <div className="info-cards">
                <div className="info-card">
                  <strong><i className="mdi mdi-file-document-outline me-2" />Documents to Prepare</strong>
                  <ul>
                    <li>Proof of Funds</li>
                    <li>Entity information and corresponding documents</li>
                  </ul>
                </div>
                <div className="info-card">
                  <strong><i className="mdi mdi-clock-outline me-2" />Time to Complete</strong>
                  <p>10-15 minutes (if you have documents ready to upload)</p>
                </div>
              </div> */}

              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                <div className="d-flex flex-wrap gap-2">
                  <span className="bid-request-section-pill">
                    <i className="mdi mdi-account-outline me-1" /> Contact
                  </span>
                  <span className="bid-request-section-pill">
                    <i className="mdi mdi-currency-inr me-1" /> Bid Request
                  </span>
                  <span className="bid-request-section-pill">
                    <i className="mdi mdi-file-document-outline me-1" /> Terms
                  </span>
                </div>
                <Button color="dark" onClick={goNext}>
                  Get Started <i className="mdi mdi-arrow-right ms-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 1 – Contact Information (stepper) */}
        {currentStep === 1 && (
          <div className="bid-request-step-content card border-0">
            <div className="card-body">
              <h5 className="mb-4"><i className="mdi mdi-account-outline me-2" /> Contact Information</h5>
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-fullName">Full Name</Label>
                    <Input id="br-fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full Name" />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-phone">Phone Number</Label>
                    <Input id="br-phone" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+91 900-000-0000" />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-email">Email</Label>
                    <Input id="br-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-state">State</Label>
                    {stateOptions.length > 0 ? (
                      <Input id="br-state" type="select" value={state} onChange={(e) => setState(e.target.value)}>
                        <option value="">Select State</option>
                        {stateOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Input>
                    ) : (
                      <Input id="br-state" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
                    )}
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-city">City</Label>
                    <Input id="br-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-pinCode">Pin Code</Label>
                    <Input id="br-pinCode" value={pinCode} onChange={(e) => setPinCode(e.target.value)} placeholder="Pin Code" />
                  </FormGroup>
                </div>
                <div className="col-12">
                  <FormGroup>
                    <Label for="br-address">Address</Label>
                    <Input id="br-address" type="textarea" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
                  </FormGroup>
                </div>
              </div>
              <div className="d-flex justify-content-between mt-4">
                <Button color="secondary" outline onClick={goBack}>
                  <i className="mdi mdi-arrow-left me-1" /> Back
                </Button>
                <Button color="dark" onClick={goNext}>
                  Next <i className="mdi mdi-arrow-right ms-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 – Bid Request (stepper) */}
        {currentStep === 2 && (
          <div className="bid-request-step-content card border-0">
            <div className="card-body">
              {/* <h5 className="mb-4"><i className="mdi mdi-currency-inr me-2" /> Bid Request</h5> */}
              <div className="bid-summary-stats">
                <div className="bid-stat-card highlight">
                  <span className="stat-label">Current Highest Bid</span>
                  <span className="stat-value">{currentHighestBid != null ? formatBidPrice(currentHighestBid) : "—"}</span>
                </div>
                <div className="bid-stat-card">
                  <span className="stat-label">Suggested increment</span>
                  <span className="stat-value">{minBidIncrease != null && Number.isFinite(minBidIncrease) && minBidIncrease > 0 ? formatBidPrice(minBidIncrease) : "—"}</span>
                </div>
                <div className="bid-stat-card">
                  <span className="stat-label">Super Area</span>
                  <span className="stat-value">{effectiveArea.toLocaleString("en-IN")} sq.ft</span>
                </div>
              </div>

              {/* Quick bid buttons */}
              <div className="quick-bid-section mb-3">
                <span className="quick-bid-label">Quick bid by price:</span>
                <div className="quick-bid-btns">
                  <button
                    type="button"
                    className={`quick-bid-btn ${Number(finalBidAmount) === quickOpt1 ? "active" : ""}`}
                    onClick={() => handleQuickBid(quickOpt1)}
                  >
                    Min ₹{quickOpt1.toLocaleString("en-IN")}
                  </button>
                  {minInc > 0 && (
                    <button
                      type="button"
                      className={`quick-bid-btn ${Number(finalBidAmount) === quickOpt2 ? "active" : ""}`}
                      onClick={() => handleQuickBid(quickOpt2)}
                    >
                      +₹{minInc.toLocaleString("en-IN")}
                    </button>
                  )}
                  {minInc > 0 && (
                    <button
                      type="button"
                      className={`quick-bid-btn ${Number(finalBidAmount) === quickOpt3 ? "active" : ""}`}
                      onClick={() => handleQuickBid(quickOpt3)}
                    >
                      +₹{(2 * minInc).toLocaleString("en-IN")}
                    </button>
                  )}
                </div>

                {isPreRented && annualRent != null && annualRent > 0 && (
                  <div className=" mb-2 mt-3">
                    <span className="quick-bid-label">Quick bid by ROI:</span>
                    {/* <div className="text-muted small mb-1">
                      Step: (min. increment ÷ starting bid) × 100 ={" "}
                      <strong>{roiStepPercent.toFixed(4)}%</strong> per click
                      {startingBidPriceForRoi > 0 && minInc > 0 && (
                        <span className="ms-1">
                          (₹{minInc.toLocaleString("en-IN")} ÷ ₹{startingBidPriceForRoi.toLocaleString("en-IN")})
                        </span>
                      )}
                    </div> */}
                    <div className="quick-bid-btns align-items-center flex-wrap gap-2">
                      <button
                        type="button"
                        className={`quick-bid-btn ${Math.abs(Number(parseIndianNumber(finalBidAmount)) - suggestedBidTotal) < 1 ? "active" : ""}`}
                        onClick={() => handleQuickBid(suggestedBidTotal)}
                        title={`Minimum bid ROI — sets total to ₹${suggestedBidTotal.toLocaleString("en-IN")}`}
                      >
                        Min ROI {minBidRoiPercent.toFixed(2)}%
                      </button>
                      <button
                        type="button"
                        className="quick-bid-btn"
                        onClick={() => handleRoiStep(-1)}
                        title={`Decrease ROI by ${roiStepPercent.toFixed(4)}% (raises total price)`}
                      >
                        − {roiStepPercent.toFixed(4)}%
                      </button>
                      <div className="roi-quick-value px-3 py-1 rounded border bg-light text-center">
                        <div className="small text-muted">ROI</div>
                        <strong className="d-block">{getCurrentRoiPercent().toFixed(2)}%</strong>
                      </div>
                      <button
                        type="button"
                        className="quick-bid-btn"
                        onClick={() => handleRoiStep(1)}
                        title={`Increase ROI by ${roiStepPercent.toFixed(4)}% (lowers total price)`}
                      >
                        + {roiStepPercent.toFixed(4)}%
                      </button>
                    </div>
                  </div>
                )}

              </div>

              <div className="row">
                {isPreRented && (
                  <div className="col-md-6">
                    <FormGroup>
                      <Label for="br-roiPercentage">ROI (%)</Label>
                      {/* <div className="small text-muted mb-1">Default (listing): {roiPercentage != null && Number.isFinite(roiPercentage) ? `${roiPercentage}%` : "—"}</div> */}
                      <Input id="br-roiPercentage" type="number" value={roiPercentageInput} onChange={(e) => handleRoiChange(e.target.value)} placeholder="e.g. 8" min={0} step={0.1} />
                    </FormGroup>
                  </div>
                )}
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-pricePerSqFt">Price per sq. ft. (₹)</Label>
                    {/* <div className="small text-muted mb-1">Default (listing): {pricePerSqFt != null && Number.isFinite(pricePerSqFt) ? `₹${Number(pricePerSqFt).toLocaleString("en-IN")}` : "—"}</div> */}
                    <InputGroup>
                      <InputGroupText>₹</InputGroupText>
                      <Input
                        id="br-pricePerSqFt"
                        type="text"
                        inputMode="decimal"
                        value={formatIndianNumber(pricePerSqFtInput)}
                        onChange={(e) => handlePricePerSqFtChange(e.target.value)}
                        placeholder="e.g. 1,00,000"
                      />
                    </InputGroup>
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-finalBidAmount">Total price (₹)</Label>
                    <InputGroup>
                      <InputGroupText>₹</InputGroupText>
                      <Input
                        id="br-finalBidAmount"
                        type="text"
                        inputMode="decimal"
                        value={formatIndianNumber(finalBidAmount)}
                        onChange={(e) => handleTotalPriceChange(e.target.value)}
                        placeholder="e.g. 50,00,000"
                      />
                    </InputGroup>
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label for="br-timeOfClosure">Time of closure *</Label>
                    <Input
                      id="br-timeOfClosure"
                      type="text"
                      value={timeOfClosure}
                      onChange={(e) => setTimeOfClosure(e.target.value)}
                      placeholder="e.g. 20 days, 3 months, 1 year"
                    />
                    {/* <small className="text-muted">Enter as you prefer: 20day, 20 days, 3 months, 1 year, 2 years, etc.</small> */}
                  </FormGroup>
                </div>
                <div className="col-12">
                  <FormGroup>
                    <Label for="br-description">Buyer's Comment (optional)</Label>
                    <Input id="br-description" type="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="No description provided" />
                  </FormGroup>
                </div>
                {isBroker && (
                  <>
                    <div className="col-md-6">
                      <FormGroup className="">
                        <Label for="br-clientFirstName">
                          Client First Name
                        </Label>
                        <Input
                          id="br-clientFirstName"
                          type="text"
                          value={clientFirstName}
                          onChange={(e) => setClientFirstName(e.target.value)}
                          placeholder="Enter client first name"
                        />
                        <small className="text-muted">Name of the client you are bidding for</small>
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <FormGroup className="">
                        <Label for="br-clientLastName">
                          Client Last Name
                        </Label>
                        <Input
                          id="br-clientLastName"
                          type="text"
                          value={clientLastName}
                          onChange={(e) => setClientLastName(e.target.value)}
                          placeholder="Enter client last name"
                        />
                      </FormGroup>
                    </div>
                  </>
                )}
              </div>
              <div className="d-flex justify-content-between mt-4">
                <Button color="secondary" outline onClick={goBack}>
                  <i className="mdi mdi-arrow-left me-1" /> Back
                </Button>
                <Button color="dark" onClick={goNext}>
                  Review Terms <i className="mdi mdi-arrow-right ms-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 – SoldX Bidding Agreement (stepper) */}
        {currentStep === 3 && (
          <div className="bid-request-step-content card border-0">
            <div className="card-body">
              <h5 className="mb-4"><i className="mdi mdi-file-document-outline me-2" /> SoldX Bidding Agreement</h5>
              <div ref={agreementRef} className="bidding-agreement mb-4" style={{ maxHeight: 400, overflowY: "auto", padding: "1.5rem", background: "#f8fafc", borderRadius: "12px", fontSize: "13px", lineHeight: "1.6" }}>
                <div className="text-center mb-4">
                  <h5 className="fw-bold text-primary">{AGREEMENT_HEADER.title}</h5>
                </div>

                <p>
                  {AGREEMENT_HEADER.intro(
                    String(new Date().getDate()),
                    new Date().toLocaleString("en-IN", { month: "long" }),
                    new Date().getFullYear().toString().slice(-2)
                  )}
                </p>
                <p>
                  <strong>SoldX</strong> (&quot;Platform&quot;), an online real estate auction platform, and<br />
                  Mr./Ms. <strong className="text-primary">{fullName || "_______________"}</strong> (&quot;Bidder&quot;), residing at <strong className="text-primary">{[address, city, state, pinCode].filter(Boolean).join(", ") || "_______________"}</strong>.
                </p>

                {userKycData.kycSelfieUrl && (
                  <div className="mb-3">
                    <strong>Bidder Photograph:</strong>
                    <div className="mt-2">
                      <img
                        src={getKycAssetUrl(userKycData.kycSelfieUrl) || ""}
                        alt="Bidder"
                        style={{ width: "80px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "2px solid #ddd" }}
                      />
                    </div>
                  </div>
                )}

                <hr />

                <h6 className="fw-bold mt-4">{BIDDER_DETAILS.sectionTitle}</h6>
                <table className="table table-sm table-borderless">
                  <tbody>
                    <tr><td style={{ width: "150px" }}>{BIDDER_DETAILS.labels.bidderName}</td><td><strong className="text-primary">{fullName || "_______________"}</strong></td></tr>
                    <tr><td>{BIDDER_DETAILS.labels.address}</td><td><strong className="text-primary">{[address, city, state, pinCode].filter(Boolean).join(", ") || "_______________"}</strong></td></tr>
                    <tr><td>{BIDDER_DETAILS.labels.auctionDate}</td><td><strong className="text-primary">{auctionDate ? formatIndianDate(auctionDate) : formatIndianDate(new Date())}</strong></td></tr>
                    <tr><td>{BIDDER_DETAILS.labels.contactDetails}</td><td><strong className="text-primary">{phoneNumber || "_______________"}</strong> | <strong className="text-primary">{email || "_______________"}</strong></td></tr>
                  </tbody>
                </table>

                <h6 className="fw-bold mt-4">{PROPERTY_DETAILS.sectionTitle}</h6>
                <p>{PROPERTY_DETAILS.intro}</p>
                <table className="table table-sm table-borderless">
                  <tbody>
                    <tr><td style={{ width: "180px" }}>{PROPERTY_DETAILS.labels.propertyName}</td><td><strong className="text-primary">{propertyTitle || "_______________"}</strong></td></tr>
                    <tr><td>{PROPERTY_DETAILS.labels.propertyAddress}</td><td><strong className="text-primary">{propertyAddress || "_______________"}</strong></td></tr>
                    <tr><td>{PROPERTY_DETAILS.labels.totalArea}</td><td><strong className="text-primary">{effectiveArea > 0 ? effectiveArea.toLocaleString("en-IN") : "_______________"}</strong></td></tr>
                    <tr><td>{PROPERTY_DETAILS.labels.startingBid}</td><td><strong className="text-primary">₹{reservePrice ? reservePrice.toLocaleString("en-IN") : startingBid}</strong></td></tr>
                    <tr><td>{PROPERTY_DETAILS.labels.yourBid}</td><td><strong className="text-success fs-6">₹{Number(finalBidAmount).toLocaleString("en-IN")}</strong></td></tr>
                  </tbody>
                </table>
                <p className="text-muted small">{PROPERTY_DETAILS.disclaimer}</p>

                <hr />

                {AGREEMENT_CLAUSES.map((clause) => (
                  <div key={clause.id}>
                    <h6 className="fw-bold mt-3">{clause.title}</h6>
                    <p>
                      {clause.id === 9 ? (
                        <>
                          {clause.text}<br />
                          <strong>Language:</strong> {DISPUTE_RESOLUTION_DETAILS.language}<br />
                          <strong>Arbitrator(s):</strong> {DISPUTE_RESOLUTION_DETAILS.arbitrators}
                        </>
                      ) : (
                        clause.text
                      )}
                    </p>
                  </div>
                ))}
              </div>

              <div className="terms-checkbox mb-4">
                <Input type="checkbox" id="br-termsAccepted" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                <Label for="br-termsAccepted" className="ms-2">
                  {CHECKBOX_AGREEMENT_TEXT}
                </Label>
              </div>

              <div className="alert alert-info small mb-4">
                <i className="mdi mdi-email me-2" />
                {EMAIL_AGREEMENT_INFO}
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <Button color="secondary" outline onClick={goBack}>
                  <i className="mdi mdi-arrow-left me-1" /> Back
                </Button>
                <Button color="primary" size="lg" className="bid-request-submit-btn" onClick={handleSubmit} disabled={submitting || !termsAccepted}>
                  {submitting ? <><i className="mdi mdi-loading mdi-spin me-2" />Submitting…</> : <><i className="mdi mdi-check me-2" />Submit Bid</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default BidRequestModal
